import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import axiosInstance from "../Utils/AxoisClient";
import { useAuth } from "./UserAuthContext";
import loginBg from "../Assets/LoginBG.png";
import loginBgFirst from "../Assets/loginbgfirst.png";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";

const Login = () => {
  const [mode, setMode] = useState("login");
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [timer, setTimer] = useState(0);
  const { login, logout } = useAuth();
  const [snackBar, setSnackBar] = useState(false);
  const navigate = useNavigate();
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [searchParams] = useSearchParams();
  const type = searchParams.get("utam_cli");
  const sessionExpired = searchParams.get("sessionExpired") === "1";
  const redirectParam = searchParams.get("redirect");
  // Only allow same-site paths (must start with a single "/", never "//"
  // which browsers treat as protocol-relative and would redirect off-site).
  const redirectTo =
    redirectParam && /^\/(?!\/)/.test(redirectParam) ? redirectParam : "/";

  /** ----------------- LOGIN ----------------- **/
  const handleLogin = async () => {
    try {
      const response = await axiosInstance.post("company/login", {
        email,
        password,
      });
      if (response.status === 200) {
        Cookies.set("_auth", response.data.ctoken, { expires: 1 });
        login();
        navigate(redirectTo);
      } else {
        logout();
        setSnackBar(true);
        setAlertSeverity("error");
        toast.error("Login failed. Please check your credentials.", {
          autoClose: 2000,
        });
        setAlertMessage("Login failed. Please check your credentials.");
      }
    } catch (error) {
      setSnackBar(true);
      setAlertSeverity("error");
      toast.error(error?.response?.data?.message, { autoClose: 2000 });
      setAlertMessage(error?.response?.data?.message || "Login failed.");
    }
  };

  useEffect(() => {
    if (type === "$2a$15$BkVVmRsU4w2da3yVLBERG.HuBS.gsQy5DWaL5YFaH1mh9Phtl4GaC") {
      setEmail("demosourcing@gmail.com");
      setPassword("demo@321");
    }
  }, [type]);

  useEffect(() => {
    if (email && password && type === "$2a$15$BkVVmRsU4w2da3yVLBERG.HuBS.gsQy5DWaL5YFaH1mh9Phtl4GaC") {
      handleLogin();
    }
  }, [email, password]);

  /** ----------------- SEND OTP ----------------- **/
  const handleSendOtp = async () => {
    try {
      const response = await axiosInstance.post("company/forgot-password", {
        email,
      });
      if (response.status === 200) {
        setSnackBar(true);
        setAlertSeverity("success");
        toast.success("OTP sent to your email.", { autoClose: 2000 });
        setAlertMessage("OTP sent to your email.");
        setStep(2);
        setTimer(120); // 2 min countdown
      } else {
        toast.error("Failed to send OTP.", { autoClose: 2000 });
        setAlertSeverity("error");
        setSnackBar(true);
        setAlertMessage("Failed to send OTP.");
      }
    } catch (error) {
      setSnackBar(true);
      setAlertSeverity("error");
      setAlertMessage(error?.response?.data?.message || "Failed to send OTP.");
      toast.error(error?.response?.data?.message || "Failed to send OTP.", {
        autoClose: 2000,
      });
    }
  };

  /** ----------------- VERIFY OTP ----------------- **/
  const handleVerifyOtp = async () => {
    try {
      const response = await axiosInstance.post("company/verify-otp", {
        email,
        otp,
      });
      if (response.status === 200) {
        setSnackBar(true);
        setAlertSeverity("success");
        toast.success("OTP verified.", { autoClose: 2000 });
        setAlertMessage("OTP verified.");
        setStep(3);
      } else {
        setSnackBar(true);
        toast.error("Invalid OTP.", { autoClose: 2000 });
        setAlertSeverity("error");
        setAlertMessage("Invalid OTP.");
      }
    } catch (error) {
      setSnackBar(true);
      toast.error("OTP verification failed.", { autoClose: 2000 });
      setAlertSeverity("error");
      setAlertMessage(error?.response?.data?.message || "Invalid OTP.");
    }
  };

  /** ----------------- RESET PASSWORD ----------------- **/
  const handleResetPassword = async () => {
    if (password !== confirmPassword) {
      setAlertSeverity("error");
      toast.error("Passwords do not match.", { autoClose: 2000 });
      setAlertMessage("Passwords do not match.");
      return;
    }
    try {
      const response = await axiosInstance.post("company/update-password", {
        email,
        confirm_password: confirmPassword,
        password,
      });
      if (response.status === 200) {
        setSnackBar(true);
        toast.success("Password reset successful. Please login.", {
          autoClose: 2000,
        });
        setAlertSeverity("success");
        setAlertMessage("Password reset successful. Please login.");
        setMode("login");
        setStep(1);
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setOtp("");
      } else {
        setSnackBar(true);
        toast.error("Failed to reset password.", { autoClose: 2000 });
        setAlertSeverity("error");
        setAlertMessage("Failed to reset password.");
      }
    } catch (error) {
      setSnackBar(true);
      setAlertSeverity("error");
      toast.error("Failed to reset password.", { autoClose: 2000 });
      setAlertMessage(
        'error?.response?.data?.message || "Failed to reset password."',
      );
    }
  };

  /** ----------------- TIMER FOR RESEND OTP ----------------- **/
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  /** ----------------- BUTTON STYLE ----------------- **/
  const btnStyle = {
    background: "linear-gradient(90deg, #7b42ff, #6435ff)",
    textTransform: "none",
    fontWeight: "bold",
    py: 1.2,
    borderRadius: "10px",
    boxShadow: "0 6px 16px rgba(100, 53, 255, 0.28)",
    transition: "box-shadow 0.15s ease, transform 0.15s ease",
    "&:hover": {
      background: "linear-gradient(90deg, #6a38e0, #562ad4)",
      boxShadow: "0 8px 20px rgba(100, 53, 255, 0.36)",
      transform: "translateY(-1px)",
    },
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      "& fieldset": { borderColor: "#e2e5ea" },
      "&:hover fieldset": { borderColor: "#c9cdd6" },
      "&.Mui-focused fieldset": { borderColor: "#6435ff", borderWidth: "1.5px" },
    },
  };

  /** ----------------- RENDER FORGOT PASSWORD FLOW ----------------- **/
  const renderForgotFlow = () => {
    if (step === 1) {
      // Step 1: Enter email
      return (
        <>
          <TextField
            placeholder="Enter your email"
            fullWidth
            variant="outlined"
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            sx={btnStyle}
            onClick={handleSendOtp}
          >
            Send OTP
          </Button>
          <Link
            component="button"
            variant="body2"
            sx={{ mt: 2 }}
            onClick={() => {
              setMode("login");
              setStep(1);
            }}
          >
            Back to login
          </Link>
        </>
      );
    }

    if (step === 2) {
      // Step 2: Verify OTP
      return (
        <>
          <TextField
            placeholder="Enter OTP"
            fullWidth
            variant="outlined"
            margin="normal"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            sx={btnStyle}
            onClick={handleVerifyOtp}
          >
            Verify OTP
          </Button>
          <Button
            variant="text"
            fullWidth
            disabled={timer > 0}
            onClick={handleSendOtp}
            sx={{ mt: 1 }}
          >
            {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
          </Button>
        </>
      );
    }

    if (step === 3) {
      // Step 3: New password
      return (
        <>
          <TextField
            placeholder="New password"
            type="password"
            fullWidth
            variant="outlined"
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <TextField
            placeholder="Confirm password"
            type="password"
            fullWidth
            variant="outlined"
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            sx={btnStyle}
            onClick={handleResetPassword}
          >
            Save Password
          </Button>
        </>
      );
    }
  };

  if (type) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            border: "4px solid #e0e0e0",
            borderTop: "4px solid #7b42ff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            "@keyframes spin": {
              "0%": { transform: "rotate(0deg)" },
              "100%": { transform: "rotate(360deg)" },
            },
          }}
        />
        <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
          Logging you in...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "auto", md: "100vh" },
        display: "flex",
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "center",
        py: { xs: 4, md: 0 },
        backgroundImage: `url(${loginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <ToastContainer position="top-right" />
      <Box
        sx={{
          width: { xs: "95%", md: "80%" }, // full width on mobile
          height: { xs: "auto", md: "80%" }, // auto height on mobile
          borderRadius: "22px",
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {sessionExpired && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 1,
              px: 2,
              background:
                "linear-gradient(90deg, rgba(234,88,12,1) 0%, rgba(249,115,22,1) 50%, rgba(234,88,12,1) 100%)",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 8v5M12 16h.01"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
                <circle
                  cx="12"
                  cy="12"
                  r="9.25"
                  stroke="#fff"
                  strokeWidth="1.6"
                  opacity="0.6"
                />
              </svg>
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#fff",
                letterSpacing: "0.01em",
              }}
            >
              Session expired — please log in again.
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, padding: { xs: 2, md: 4 } }}>
        <Grid container sx={{ height: { xs: "auto", md: "100%" } }}>
          {/* Left Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              background: "transparent",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              position: "relative",
              borderRadius: 2,
              backgroundImage: `url(${loginBgFirst})`,
              backgroundSize: "cover",
              backgroundPosition: "center 20%",
              height: { xs: "200px", md: "100%" }, // smaller height for mobile
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                alignItems: "center",
                justifyContent: "flex-start",
                lineHeight: 1.3,
                p: { xs: 2, md: 3 },
                fontSize: { xs: "20px", md: "h4.fontSize" }, // responsive font size
              }}
            >
              Your One Stop <br /> Procurement Solution
            </Typography>
          </Grid>

          {/* Right Section */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              p: { xs: 2, md: 4 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              position: "relative",
              backgroundColor: "#fff",
            }}
          >
            {/* Logo */}
            {/* Logo */}
            <Box
              sx={{
                position: "absolute",
                top: { xs: 10, md: 16 },
                right: { xs: 10, md: 20 },
                width: { xs: 100, md: 120 },
                cursor: "pointer",
              }}
              onClick={() =>
                window.open("https://www.procurementresource.com/", "_blank")
              }
            >
              <img
                src="https://360.procurementresource.com/assets/images/prlogo.webp"
                alt="Logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Box>

            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: "'Lora', serif",
                fontWeight: 600,
                fontSize: { xs: "28px", md: "35px" },
                lineHeight: 1.3,
              }}
            >
              {mode === "login" ? "Get Started" : "Reset Password"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Inter', sans-serif",
                color: "text.secondary",
              }}
              mb={3}
            >
              {mode === "login"
                ? "Welcome to Sourcing Compass"
                : step === 1
                  ? "Enter your email to receive OTP"
                  : step === 2
                    ? "Enter OTP sent to your email"
                    : "Create a new password"}
            </Typography>

            {mode === "login" ? (
              <>
                <TextField
                  placeholder="Your email"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={inputStyle}
                />
                <TextField
                  placeholder="Your password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ ...inputStyle, background: "#e8f0fe" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Link
                  component="button"
                  variant="body2"
                  sx={{ alignSelf: "flex-end", color: "red", mb: 2 }}
                  onClick={() => setMode("forgot")}
                >
                  Forget password
                </Link>
                <Button
                  variant="contained"
                  fullWidth
                  sx={btnStyle}
                  onClick={handleLogin}
                >
                  Login
                </Button>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "10px",
                  }}
                >
                  <a
                    href="https://www.procurementresource.com/contact-us"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "none",
                      color: "#007bff",
                      fontWeight: "500",
                    }}
                  >
                    Have Questions? Contact Us
                  </a>
                </div>
              </>
            ) : (
              renderForgotFlow()
            )}
          </Grid>
        </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
